import { Close, CloseRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  useTheme,
} from '@mui/material';
import { useAppSelector } from 'app/config/store';
import { getEntities } from 'app/entities/inspection-item/inspection-item.reducer';
import { extractOnlyIds } from 'app/shared/util/functions-util';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { useDispatch } from 'react-redux';

type InspectionItemSelectionModalProps = {
  selectedInspectionItems: any[];
  setSelectedInspectionItems: any;
  setModalOpen: any;
};

export const InspectionItemModal = React.memo(({ selectedInspectionItems, setSelectedInspectionItems, setModalOpen }: InspectionItemSelectionModalProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [currentSelectedInspectionItems, setCurrentSelectedInspectionItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedInspectionItemsFirst, setSelectedInspectionItemsFirst] = useState([]);

  const inspectionItems = useAppSelector(state => state.inspectionItem.entities);

  const filteredInspectionItems = inspectionItems?.filter(inspectionItem => inspectionItem.name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    dispatch(getEntities({ page: 0, size: 10000, sort: 'name', nameSearch: '' }));
  }, []);

  useEffect(() => {
    setSelectedInspectionItemsFirst(extractOnlyIds(selectedInspectionItems));
    setCurrentSelectedInspectionItems(extractOnlyIds(selectedInspectionItems));
  }, [selectedInspectionItems]);

  useEffect(() => {
    currentSelectedInspectionItems?.length === inspectionItems?.length ? setSelectAllChecked(true) : setSelectAllChecked(false);
  }, [currentSelectedInspectionItems, inspectionItems]);

  const filteredInspectionItemsSorted = filteredInspectionItems.sort((a, b) => {
    const aSelected = selectedInspectionItemsFirst.includes(a.id);
    const bSelected = selectedInspectionItemsFirst.includes(b.id);
    if (aSelected && !bSelected) {
      return -1;
    } else if (!aSelected && bSelected) {
      return 1;
    } else {
      return 0;
    }
  });

  const handleSelectInspectionItem = inspectionItemId => {
    const index = currentSelectedInspectionItems.indexOf(inspectionItemId);
    if (index === -1) {
      setCurrentSelectedInspectionItems([...currentSelectedInspectionItems, inspectionItemId]);
    } else {
      const updatedSelectedInspectionItems = [...currentSelectedInspectionItems];
      updatedSelectedInspectionItems.splice(index, 1);
      setCurrentSelectedInspectionItems(updatedSelectedInspectionItems);
    }
  };

  const handleSave = () => {
    setSelectedInspectionItems(currentSelectedInspectionItems);
    setModalOpen(false);
  };

  const toggleModal = () => {
    setCurrentSelectedInspectionItems(extractOnlyIds(selectedInspectionItems));
    setSearchTerm('');
    setModalOpen(prev => !prev);
  };

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setCurrentSelectedInspectionItems([]);
    } else {
      setCurrentSelectedInspectionItems(filteredInspectionItems.map(inspectionItem => inspectionItem.id));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={true}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {translate('fleetSenseApp.inspectionItem.modalTitle')}
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={translate('fleetSenseApp.inspectionItem.search')}
              InputProps={{
                sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                endAdornment: (
                  <InputAdornment position="end">
                    {!searchTerm ? (
                      <SearchIcon color="action" />
                    ) : (
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <CloseIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {!!filteredInspectionItemsSorted?.length && (
            <FormControlLabel control={<Checkbox checked={selectAllChecked} onChange={handleSelectAll} />} label="Selecionar Todos" />
          )}
          {filteredInspectionItemsSorted.map(inspectionItem => (
            <Box key={inspectionItem.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentSelectedInspectionItems.indexOf(inspectionItem.id) !== -1}
                    onChange={() => handleSelectInspectionItem(inspectionItem.id)}
                  />
                }
                label={inspectionItem.name}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.cancel')}
          </Button>
          <Button onClick={handleSave} disableRipple variant="contained" color="secondary">
            {translate('fleetSenseApp.truckDeviceStatus.frontEnd.buttonNames.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
});
